import {
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Label,
  showNotification,
  Tooltip,
} from 'platform/components';
import {VStack} from 'platform/foundation';

import {head} from 'ramda';

import {
  type UpdateSignatureRequestBody,
  useCreateSignatureMutation,
  useListSignaturesQuery,
  useRemoveSignatureMutation,
  useUpdateSignatureMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {
  EMAIL_TOOLBAR_OPTIONS,
  handleApiError,
  isEmailHtmlEmpty,
  normalizeEmailHtml,
  useEmailBodyImageUpload,
} from '@dms/shared';

import {ApiError} from 'shared/web';

import {MyProfileFooter} from '../../components/MyProfileFooter';
import {MyProfileTemplate} from '../../components/MyProfileTemplate/MyProfileTemplate';

type FormValues = {
  signature: string;
};

export function EmailSignature() {
  const {data: signatures, isLoading, isError} = useListSignaturesQuery();

  const [createSignature] = useCreateSignatureMutation();
  const [updateSignature] = useUpdateSignatureMutation();
  const [removeSignature] = useRemoveSignatureMutation();
  const {handleUploadImage, isUploadingImages, ensureBodyReadyToSend} = useEmailBodyImageUpload();

  const usedSignature = head(signatures ?? []);

  const handleSubmit: FormSubmitHandler<FormValues> = async (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    if (!ensureBodyReadyToSend(values.signature, blockerContext)) {
      return;
    }

    if (usedSignature && isEmailHtmlEmpty(values.signature)) {
      await removeSignature({id: usedSignature.id})
        .unwrap()
        .then(() => {
          showNotification.success(i18n.t('general.notifications.successfullyDeleted'));
          reset(undefined, {keepValues: true});
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );

      return;
    }

    const signatureRequestBody: UpdateSignatureRequestBody = {
      signature: normalizeEmailHtml(values.signature, {allowsDataImages: true}),
      contentType: 'html',
    };

    if (usedSignature) {
      await updateSignature({
        id: usedSignature.id,
        updateSignatureRequestBody: signatureRequestBody,
      })
        .unwrap()
        .then(() => {
          showNotification.success(i18n.t('general.notifications.successfullyCreated'));
          reset(undefined, {keepValues: true});
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );

      return;
    }

    return createSignature({updateSignatureRequestBody: signatureRequestBody})
      .unwrap()
      .then(() => {
        reset(undefined, {keepValues: true});
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const defaultValues = {
    signature: usedSignature?.signature,
  };

  return (
    <MyProfileTemplate
      header={{
        title: i18n.t('page.myProfile.labels.emailSignature'),
        breadcrumbs: [
          {label: i18n.t('entity.user.labels.myProfile'), isCurrentPage: true},
          {label: i18n.t('page.myProfile.labels.emailSignature'), isCurrentPage: true},
        ],
      }}
    >
      <DataStatus isLoading={isLoading} isError={isError} spacing={10}>
        <Form<FormValues>
          shouldWatchForUnsavedChanges
          onSubmit={handleSubmit}
          defaultValues={defaultValues}
        >
          {(control, formApi) => (
            <VStack spacing={2} maxWidth={200}>
              <Label>{i18n.t('page.myProfile.labels.emailSignatureDescription')}</Label>

              <FormField
                type="richText"
                name="signature"
                label={i18n.t('page.myProfile.labels.emailSignature')}
                control={control}
                toolbarOptions={EMAIL_TOOLBAR_OPTIONS}
                handleUploadImage={handleUploadImage}
                minRows={6}
              />

              <MyProfileFooter
                actions={[
                  {
                    type: 'button',
                    onClick: () => formApi.reset(defaultValues),
                    title: i18n.t('general.actions.discardChanges'),
                    variant: 'secondary',
                  },
                  {
                    type: 'custom',
                    component: (
                      <Tooltip
                        label={i18n.t('richText.toolbar.uploadingImages')}
                        placement="top"
                        isDisabled={!isUploadingImages}
                      >
                        <FormButton
                          control={control}
                          type="submit"
                          isDisabled={isUploadingImages}
                          title={
                            usedSignature
                              ? i18n.t('general.actions.saveChanges')
                              : i18n.t('general.actions.create')
                          }
                          data-testid="emailSignature-submitButton"
                        />
                      </Tooltip>
                    ),
                  },
                ]}
              />
            </VStack>
          )}
        </Form>
      </DataStatus>
    </MyProfileTemplate>
  );
}
